import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth.store';
interface Session {
  id: string;
  tournamentId: string;
  tournamentName: string;
  state: string;
  version: number;
  quota: number;
  waitlistLimit: number;
  bookingOpenAt: string;
  bookingCloseAt: string;
  cancellationCutoffAt: string;
  checkInOpenAt: string;
  checkInCutoffAt: string;
  qualificationExpiresAt: string;
}
interface Row {
  id: string;
  tournamentId: string;
  hasCanonicalRegistration: boolean;
  status: string;
  version: number;
  member?: { nickname: string; associationMemberNo: string | null };
}
interface Page<T> {
  items: T[];
  nextCursor: string | null;
}
const dateFields = [
  'bookingOpenAt',
  'bookingCloseAt',
  'cancellationCutoffAt',
  'checkInOpenAt',
  'checkInCutoffAt',
  'qualificationExpiresAt',
] as const;
const local = (v: string) => {
  const d = new Date(v);
  return new Date(d.getTime() + 8 * 3600000).toISOString().slice(0, 16);
};
export default function ReservationsPage() {
  const sessionRevision = useAuthStore((s) => s.sessionRevision);
  return <ReservationWorkspace key={sessionRevision} sessionRevision={sessionRevision} />;
}
function ReservationWorkspace({ sessionRevision }: { sessionRevision: number }) {
  const { t } = useTranslation('member');
  const cache = useQueryClient();
  const actions = useAuthStore((s) => s.allowedActions);
  const canRead = actions?.includes('reservation:read') ?? false;
  const canManage = actions?.includes('reservation:manage') ?? false;
  const canRegister = actions?.includes('tournament:register') ?? false;
  const canCheck = actions?.includes('reservation:checkin') ?? false;
  const [selected, setSelected] = useState<string>('');
  const [cursor, setCursor] = useState<string | null>(null);
  const [rowCursor, setRowCursor] = useState<string | null>(null);
  const [edit, setEdit] = useState<Session | null>(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const writing = useRef(false);
  const [form, setForm] = useState<Record<string, string>>({ quota: '1', waitlistLimit: '0' });
  const [candidateFor, setCandidateFor] = useState<Row | null>(null);
  const [entryId, setEntryId] = useState('');
  const pending = useRef<{ path: string; body: object; key: string } | null>(null);
  const [retry, setRetry] = useState(false);
  const sessions = useQuery({
    queryKey: ['reservation-sessions', sessionRevision, cursor],
    queryFn: () =>
      api.get<Page<Session>>(
        '/reservation-sessions' + (cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''),
      ),
    enabled: canRead,
  });
  const rows = useQuery({
    queryKey: ['reservation-rows', sessionRevision, selected, rowCursor],
    queryFn: () =>
      api.get<Page<Row>>(
        `/reservations?sessionId=${selected}` +
          (rowCursor ? `&cursor=${encodeURIComponent(rowCursor)}` : ''),
      ),
    enabled: canRead && !!selected,
  });
  const tournaments = useQuery({
    queryKey: ['reservation-tournaments', sessionRevision],
    queryFn: () => api.get<{ data: { id: string; name: string }[] }>('/tournament?limit=100'),
    enabled: canManage,
  });
  const candidates = useQuery({
    queryKey: ['reservation-candidates', sessionRevision, candidateFor?.id],
    queryFn: () =>
      api.get<{ id: string; playerName: string; registeredAt: string }[]>(
        `/reservations/${candidateFor!.id}/registration-candidates`,
      ),
    enabled: canCheck && !!candidateFor,
  });
  async function execute(path: string, body: object, put = false) {
    if (writing.current) return;
    writing.current = true;
    setBusy(true);
    setError(false);
    try {
      if (put) await api.put(path, body);
      else await api.post(path, body);
      pending.current = null;
      setRetry(false);
      setCandidateFor(null);
      await cache.invalidateQueries({ queryKey: ['reservation-sessions'] });
      await cache.invalidateQueries({ queryKey: ['reservation-rows'] });
    } catch (error) {
      setError(true);
      const code = isAxiosError(error) ? error.response?.data?.errorCode : undefined;
      if (
        typeof code === 'string' &&
        [
          'CANONICAL_REGISTRATION_EXISTS',
          'STALE_VERSION',
          'INVALID_STATE',
          'WINDOW_CLOSED',
          'NO_SHOW_TOO_EARLY',
          'MEMBER_INELIGIBLE',
          'OPERATION_KEY_CONFLICT',
        ].includes(code)
      ) {
        pending.current = null;
        setRetry(false);
        await cache.invalidateQueries({ queryKey: ['reservation-rows'] });
      } else if (pending.current) setRetry(true);
    } finally {
      writing.current = false;
      setBusy(false);
    }
  }
  function transition(
    row: Row,
    kind: 'arrive' | 'cancel-arrival' | 'check-in' | 'no-show',
    entry?: string,
  ) {
    const key = `${row.id}:${row.version}:${kind}`;
    if (pending.current && pending.current.key !== key) return;
    pending.current ??= {
      key,
      path: `/reservations/${row.id}/${kind}`,
      body: {
        version: row.version,
        operationKey: crypto.randomUUID(),
        ...(entry ? { entryId: entry } : {}),
      },
    };
    void execute(pending.current.path, pending.current.body);
  }
  function chooseEdit(s: Session) {
    setEdit(s);
    setForm({
      tournamentId: s.tournamentId,
      quota: String(s.quota),
      waitlistLimit: String(s.waitlistLimit),
      ...Object.fromEntries(dateFields.map((k) => [k, local(s[k])])),
    });
  }
  function save() {
    const body = {
      tournamentId: form.tournamentId,
      quota: Number(form.quota),
      waitlistLimit: Number(form.waitlistLimit),
      ...Object.fromEntries(
        dateFields.map((k) => [k, new Date(`${form[k]}:00+08:00`).toISOString()]),
      ),
      ...(edit ? { version: edit.version } : {}),
    };
    void execute(edit ? `/reservation-sessions/${edit.id}` : '/reservation-sessions', body, !!edit);
  }
  if (!canRead)
    return (
      <AdminPage header={<AdminPageHeader title={t('reservationStaffTitle')} />}>
        {' '}
        <p role="alert">{t('reservationStaffDenied')}</p>{' '}
      </AdminPage>
    );
  return (
    <AdminPage
      className="admin-page-body grid gap-4 text-base-content"
      header={<AdminPageHeader title={t('reservationStaffTitle')} />}
    >
      <p>{t('reservationStaffBoundary')}</p>
      {(error || sessions.isError || rows.isError) && (
        <p role="alert" className="text-error">
          {t('reservationStaffError')}
        </p>
      )}
      {retry && pending.current && (
        <button
          className="px-4 py-2 bg-primary text-primary-content rounded disabled:opacity-40"
          disabled={busy}
          onClick={() => void execute(pending.current!.path, pending.current!.body)}
        >
          {t('reservationStaffRetry')}
        </button>
      )}
      <button
        className="px-4 py-2 border border-base-300 rounded hover:bg-base-200 disabled:opacity-40"
        disabled={busy}
        onClick={() => {
          void sessions.refetch();
          void rows.refetch();
        }}
      >
        {t('reservationStaffRefresh')}
      </button>
      <section className="grid gap-3">
        {sessions.data?.items.map((s) => (
          <article className="border border-base-300 rounded p-3 grid gap-2" key={s.id}>
            <h2>
              {s.tournamentName} — {s.state}
            </h2>
            <p>
              {t('reservationStaffQuota')}: {s.quota}; {t('reservationStaffWaitlist')}:{' '}
              {s.waitlistLimit}
            </p>
            <button
              className="px-4 py-2 border border-base-300 rounded hover:bg-base-200 disabled:opacity-40"
              onClick={() => {
                setSelected(s.id);
                setRowCursor(null);
              }}
            >
              {t('reservationStaffView')}
            </button>
            {canManage && s.state !== 'CANCELLED' && (
              <div className="flex flex-wrap gap-2">
                <button
                  className="px-4 py-2 border border-base-300 rounded hover:bg-base-200 disabled:opacity-40"
                  disabled={busy || retry}
                  onClick={() => chooseEdit(s)}
                >
                  {t('reservationStaffEdit')}
                </button>
                {['OPEN', 'CLOSED', 'CANCELLED']
                  .filter((state) => state !== s.state)
                  .map((state) => (
                    <button
                      className="px-4 py-2 border border-base-300 rounded hover:bg-base-200 disabled:opacity-40"
                      key={state}
                      disabled={busy || retry}
                      onClick={() => {
                        if (
                          state !== 'CANCELLED' ||
                          window.confirm(t('reservationStaffCancelConfirm'))
                        )
                          void execute(`/reservation-sessions/${s.id}/state`, {
                            state,
                            version: s.version,
                          });
                      }}
                    >
                      {t(`reservationStaffState_${state}`)}
                    </button>
                  ))}
              </div>
            )}
          </article>
        ))}
        {sessions.data?.nextCursor && (
          <button
            className="px-4 py-2 border border-base-300 rounded hover:bg-base-200 disabled:opacity-40"
            onClick={() => setCursor(sessions.data!.nextCursor)}
          >
            {t('reservationStaffMore')}
          </button>
        )}
      </section>
      {selected && (
        <section className="grid gap-2" aria-label={t('reservationStaffView')}>
          {rows.data?.items.map((r) => (
            <article className="border border-base-300 rounded p-3" key={r.id}>
              <p>
                {r.member?.nickname} {r.member?.associationMemberNo} —{' '}
                {t(`reservationStaffStatus_${r.status}`)}
              </p>
              {canCheck && ['CONFIRMED', 'ARRIVED'].includes(r.status) && (
                <div className="flex gap-2">
                  {r.status === 'CONFIRMED' && (
                    <button
                      className="btn btn-primary"
                      disabled={busy || retry}
                      onClick={() => transition(r, 'arrive')}
                    >
                      {t('reservationStaffArrive')}
                    </button>
                  )}
                  <button
                    className="px-4 py-2 border border-base-300 rounded hover:bg-base-200 disabled:opacity-40"
                    disabled={busy || retry}
                    onClick={() => {
                      setCandidateFor(r);
                      setEntryId('');
                    }}
                  >
                    {t('reservationStaffCheckin')}
                  </button>
                  {r.status === 'CONFIRMED' && (
                    <button
                      className="px-4 py-2 border border-base-300 rounded hover:bg-base-200 disabled:opacity-40"
                      disabled={busy || retry}
                      onClick={() => transition(r, 'no-show')}
                    >
                      {t('reservationStaffNoShow')}
                    </button>
                  )}
                </div>
              )}
              {r.status === 'ARRIVED' && (
                <div className="grid gap-2">
                  <p>
                    {t(
                      r.hasCanonicalRegistration
                        ? 'reservationStaffRegistrationExists'
                        : 'reservationStaffPendingRegistration',
                    )}
                  </p>
                  {canRegister && r.tournamentId && !r.hasCanonicalRegistration && (
                    <Link className="btn btn-primary" to={`/tournament/${r.tournamentId}/register`}>
                      {t('reservationStaffRegister')}
                    </Link>
                  )}
                  {canManage && (
                    <button
                      className="btn btn-outline"
                      disabled={busy || retry || r.hasCanonicalRegistration}
                      onClick={() => transition(r, 'cancel-arrival')}
                    >
                      {t('reservationStaffCancelArrival')}
                    </button>
                  )}
                </div>
              )}
            </article>
          ))}
          {rows.data?.nextCursor && (
            <button
              className="px-4 py-2 border border-base-300 rounded hover:bg-base-200 disabled:opacity-40"
              onClick={() => setRowCursor(rows.data!.nextCursor)}
            >
              {t('reservationStaffMore')}
            </button>
          )}
        </section>
      )}
      {candidateFor && (
        <section className="grid gap-2 border border-base-300 p-3">
          <h2>{t('reservationStaffExistingEntry')}</h2>
          <p>{t('reservationStaffNoAdapter')}</p>
          <select
            className="w-full px-3 py-2 border border-base-300 rounded"
            aria-label={t('reservationStaffExistingEntry')}
            value={entryId}
            onChange={(e) => setEntryId(e.target.value)}
          >
            <option value="">{t('reservationStaffSelect')}</option>
            {candidates.data?.map((e) => (
              <option key={e.id} value={e.id}>
                {e.playerName} —{' '}
                {new Date(e.registeredAt).toLocaleString(undefined, { timeZone: 'Asia/Taipei' })}
              </option>
            ))}
          </select>
          <button
            className="px-4 py-2 bg-primary text-primary-content rounded disabled:opacity-40"
            disabled={!entryId || busy || retry}
            onClick={() => transition(candidateFor, 'check-in', entryId)}
          >
            {t('reservationStaffCheckin')}
          </button>
        </section>
      )}
      {canManage && (
        <form
          className="grid gap-3 border border-base-300 rounded p-4"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <h2>{t(edit ? 'reservationStaffEdit' : 'reservationStaffCreate')}</h2>
          <button
            type="button"
            className="px-4 py-2 border border-base-300 rounded hover:bg-base-200 disabled:opacity-40"
            onClick={() => {
              setEdit(null);
              setForm({ quota: '1', waitlistLimit: '0' });
            }}
          >
            {t('reservationStaffCreate')}
          </button>
          <label>
            {t('reservationStaffTournament')}
            <select
              required
              className="select select-bordered w-full"
              disabled={!!edit}
              value={form.tournamentId ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, tournamentId: e.target.value }))}
            >
              <option value="">{t('reservationStaffSelect')}</option>
              {tournaments.data?.data.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </select>
          </label>
          {['quota', 'waitlistLimit'].map((k) => (
            <label key={k}>
              {t(k === 'quota' ? 'reservationStaffQuota' : 'reservationStaffWaitlist')}
              <input
                required
                className="input input-bordered w-full"
                type="number"
                min={k === 'quota' ? 1 : 0}
                max={10000}
                value={form[k] ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
              />
            </label>
          ))}
          <p>{t('reservationStaffTimezone')}</p>
          {dateFields.map((k) => (
            <label key={k}>
              {t(`reservationStaff_${k}`)}
              <input
                required
                className="input input-bordered w-full"
                type="datetime-local"
                value={form[k] ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
              />
            </label>
          ))}
          <button
            className="px-4 py-2 bg-primary text-primary-content rounded disabled:opacity-40"
            disabled={busy || retry}
            type="submit"
          >
            {t('reservationStaffSave')}
          </button>
        </form>
      )}
    </AdminPage>
  );
}
